import {
    PRE_DEFINED_CREATE_FIELD,
    PRE_DEFINED_FIELD,
    PRE_DEFINED_UPDATE_FIELD,
    TECHNOGRAPHICS_FIELD,
} from "app/pages/sales-intelligence/common-components/export-table/const";
import {
    PREDEFINED_CREATE_ITEM_USER_TEXT,
    PREDEFINED_ITEM_USER_TEXT,
    PREDEFINED_UPDATE_ITEM_USER_TEXT,
    TECHNOGRAPHICS_ITEM_USER_TEXT,
} from "app/pages/sales-intelligence/common-components/export-table/constants";
import { getPredefinedMetadataOnCrmFieldChange } from "app/pages/sales-intelligence/common-components/export-table/utils";
import {
    getAccountsFields,
    getAccountsSourceFieldsConfig,
    getScoringFieldKeys,
} from "app/pages/sales-intelligence/pages/manage-integration/configs/accountsFields";
import { getCompanyAsLeadsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/companyAsLeadsFields";
import { getContactsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/contactsFields";
import { getLeadsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/leadsFields";
import { getOpportunityListFieldsKeys } from "app/pages/sales-intelligence/pages/manage-integration/configs/opportunityListFields";
import { getRequesterFieldsKeys } from "app/pages/sales-intelligence/pages/manage-integration/configs/requesterFields";
import { getSubsidiaryFieldsKeys } from "app/pages/sales-intelligence/pages/manage-integration/configs/subsidiaryFields";
import type { ColumnItem } from "app/pages/sales-intelligence/common-components/export-table/types";

import type { ExportName, ExportSetting, NewExportSetting, SimilarwebFieldsConfig } from "../store/type";
import { ExportSettingsSourceType } from "../store/type";
import type { Integration } from "../types";
import { CrmImportSetting } from "../types";

type UpdateSettingsType = {
    settings: ExportSetting[];
    oldSetting: ExportSetting;
    exportName: ExportName;
    newSetting: NewExportSetting;
    columns: ColumnItem[];
    isCrmEnrichmentEnabled?: boolean;
};

export const getConfigByExportName = (integrationName: Integration, exportName: ExportName) => {
    const configs = {
        accounts: getAccountsFields(),
        leads: getLeadsFields(),
        companyAsLeads: getCompanyAsLeadsFields(),
        contacts: getContactsFields(integrationName),
    };

    return configs[exportName];
};

export const getExportSettingTitle = (
    metadata: string | undefined,
    sourceType: ExportSettingsSourceType | undefined,
    config: SimilarwebFieldsConfig,
) => {
    switch (sourceType) {
        case ExportSettingsSourceType.Technologies:
            return TECHNOGRAPHICS_ITEM_USER_TEXT;
        case ExportSettingsSourceType.Predefined:
            return PREDEFINED_ITEM_USER_TEXT;
        case ExportSettingsSourceType.PredefinedCreate:
            return PREDEFINED_CREATE_ITEM_USER_TEXT;
        case ExportSettingsSourceType.PredefinedUpdate:
            return PREDEFINED_UPDATE_ITEM_USER_TEXT;
        default:
            return metadata ? config[metadata]?.name || "" : "";
    }
};

export const isFieldEligibleForEnrichment = (sourceType: ExportSettingsSourceType): boolean =>
    sourceType !== ExportSettingsSourceType.DataRequester &&
    sourceType !== ExportSettingsSourceType.Predefined &&
    sourceType !== ExportSettingsSourceType.PredefinedCreate &&
    sourceType !== ExportSettingsSourceType.PredefinedUpdate &&
    sourceType !== ExportSettingsSourceType.StaticList;

export const updateSettings = ({
    settings,
    oldSetting,
    exportName,
    newSetting,
    columns,
    isCrmEnrichmentEnabled = false,
}: UpdateSettingsType): ExportSetting[] => {
    const config = getConfigByExportName("hubspot", exportName);
    const {
        overwrite: newOverwrite,
        externalField: newCrmField,
        metadata: newMetadata,
        field: newField = "",
        sourceType: newSourceType,
    } = newSetting;

    const newSwFieldConfig = newMetadata ? config[newMetadata] : "";
    return settings.map((setting) => {
        if (oldSetting.field && setting.field === oldSetting.field) {
            const field = newField || oldSetting.field;

            const overwrite =
                typeof newOverwrite === "boolean" ? newOverwrite : setting.overwrite || oldSetting?.overwrite;

            const externalField =
                typeof newCrmField === "string" ? newCrmField : setting.externalField || oldSetting?.externalField;

            const title = getExportSettingTitle(newMetadata, newSourceType || oldSetting.sourceType, config);

            let metadata = "metadata" in newSetting ? newMetadata : oldSetting.metadata;
            let example = newSwFieldConfig ? newSwFieldConfig.example : oldSetting.example;
            if (
                newSourceType === ExportSettingsSourceType.Predefined ||
                newSourceType === ExportSettingsSourceType.PredefinedCreate ||
                newSourceType === ExportSettingsSourceType.PredefinedUpdate ||
                newSourceType === ExportSettingsSourceType.Technologies
            ) {
                metadata = "";
                example = "";
            }

            if (
                newCrmField &&
                (setting.sourceType === ExportSettingsSourceType.Predefined ||
                    setting.sourceType === ExportSettingsSourceType.PredefinedCreate ||
                    setting.sourceType === ExportSettingsSourceType.PredefinedUpdate)
            ) {
                const updatedMetadata = getPredefinedMetadataOnCrmFieldChange(
                    newCrmField,
                    oldSetting.externalField,
                    columns,
                );

                if (updatedMetadata !== null) {
                    metadata = updatedMetadata;
                }
            }

            const updatedSetting = {
                field,
                example,
                externalField,
                overwrite,
                metadata,
                title: title ? title : setting.title,
                sourceType: newSourceType || setting.sourceType,
                type: setting.type,
                forEnrichment: setting.forEnrichment,
                forExport: setting.forExport,
            };

            if (isCrmEnrichmentEnabled) {
                if (newSourceType) {
                    return {
                        ...updatedSetting,
                        forExport: true,
                        forEnrichment: isFieldEligibleForEnrichment(newSourceType),
                    };
                }
                return {
                    ...updatedSetting,
                    forExport: typeof newSetting.forExport === "boolean" ? newSetting.forExport : setting.forExport,
                    forEnrichment:
                        typeof newSetting.forEnrichment === "boolean"
                            ? newSetting.forEnrichment
                            : setting.forEnrichment,
                };
            }

            return updatedSetting;
        }
        return setting;
    });
};

// Check if some of the settings changed by user
const calcIsChanged = (currSettings: ExportSetting[], prevSettings: ExportSetting[]): boolean => {
    if (currSettings.length !== prevSettings.length) {
        return true;
    }

    const sortingExportSettings = (
        { field: firstField }: ExportSetting,
        { field: secondField }: ExportSetting,
    ): number => {
        if (firstField > secondField) {
            return 1;
        }

        return -1;
    };

    const sortedCurrSettings = [...currSettings].sort(sortingExportSettings);
    const sortedPrevSettings = [...prevSettings].sort(sortingExportSettings);

    return sortedCurrSettings.some((setting, index) => {
        const { externalField, overwrite, metadata, forEnrichment, forExport } = setting;
        const {
            externalField: prevCrmField,
            overwrite: prevOverwrite,
            metadata: prevMetadata,
            forExport: prevForExport,
            forEnrichment: prevForEnrichment,
        } = sortedPrevSettings[index];

        if (externalField !== prevCrmField || overwrite !== prevOverwrite) {
            return true;
        }

        if (metadata && metadata !== prevMetadata) {
            return true;
        }

        if (prevForExport !== forExport) {
            return true;
        }

        if (prevForEnrichment !== forEnrichment) {
            return true;
        }

        return false;
    });
};

export const isEmptyPreDefined = (settings: ExportSetting[]): boolean => {
    return settings.some(({ field, metadata }) => {
        if (field) {
            if (field.includes(PRE_DEFINED_FIELD)) {
                return !metadata;
            }
        }
        return false;
    });
};

const isIgnoredSettingName = (settingName: ExportName, isContactsVisible: boolean) => {
    const ignoredSettingNames: ExportName[] = isContactsVisible ? [] : ["contacts", "leads"];

    return ignoredSettingNames.includes(settingName);
};

export const getTabsWithEmptyFields = ({
    allExportSettings,
    isContactsVisible,
}: {
    allExportSettings: Record<string, ExportSetting[]>;
    isContactsVisible: boolean;
}): ExportName[] => {
    return Object.keys(allExportSettings).reduce<ExportName[]>((acc, exportSettingName) => {
        const settingName = exportSettingName as ExportName;
        const currentSettings = allExportSettings[settingName];

        if (!currentSettings.length || isIgnoredSettingName(settingName, isContactsVisible)) {
            return acc;
        }

        currentSettings.forEach(({ externalField, metadata, field }) => {
            if (!externalField) {
                acc.push(settingName);
            }

            if (field && (field.includes(PRE_DEFINED_FIELD) || field.includes(TECHNOGRAPHICS_FIELD)) && !metadata) {
                acc.push(settingName);
            }
        });

        return acc;
    }, []);
};

export const getChangedTabNames = (
    settings: Record<string, ExportSetting[]>,
    prevSettings: Record<string, ExportSetting[]>,
): ExportName[] => {
    const tabNames = Object.keys(settings) as ExportName[];

    return tabNames.reduce<ExportName[]>((acc, tabName) => {
        const isChanged = calcIsChanged(settings[tabName], prevSettings[tabName]);
        if (isChanged) {
            acc.push(tabName);
        }

        return acc;
    }, []);
};

const convertIntegrationTypeBase = (typeName: ExportName, companyAsLeadsReturn: string) => {
    if (typeName === "companyAsLeads") {
        return companyAsLeadsReturn;
    }
    if (typeName && typeName[typeName.length - 1] === "s") {
        return typeName.slice(0, -1);
    }
    return "";
};

export const convertIntegrationTypeForApi = (typeName: ExportName) => {
    return convertIntegrationTypeBase(typeName, "lead");
};

export const convertIntegrationTypeForApiSave = (typeName: ExportName) => {
    return convertIntegrationTypeBase(typeName, "company-as-lead");
};

export const getActiveImportSettings = (
    isMonitorEnabled: boolean,
    isExcludeEnabled: boolean,
    isDisplayEnabled: boolean,
): CrmImportSetting[] => {
    return [
        [isMonitorEnabled, CrmImportSetting.Monitor],
        [isExcludeEnabled, CrmImportSetting.Exclude],
        [isDisplayEnabled, CrmImportSetting.Display],
    ].reduce<CrmImportSetting[]>(
        (settings, [isEnabled, setting]: [boolean, CrmImportSetting]) =>
            isEnabled ? settings.concat(setting) : settings,
        [],
    );
};

export const getExportSettingSourceType = (setting: string) => {
    if (setting.includes(PRE_DEFINED_CREATE_FIELD)) {
        return ExportSettingsSourceType.PredefinedCreate;
    }
    if (setting.includes(PRE_DEFINED_UPDATE_FIELD)) {
        return ExportSettingsSourceType.PredefinedUpdate;
    }
    if (setting.includes(PRE_DEFINED_FIELD)) {
        return ExportSettingsSourceType.Predefined;
    }
    if (setting.includes(TECHNOGRAPHICS_FIELD)) {
        return ExportSettingsSourceType.Technologies;
    }
    if (getRequesterFieldsKeys().includes(setting)) {
        return ExportSettingsSourceType.DataRequester;
    }
    if (getSubsidiaryFieldsKeys().includes(setting)) {
        return ExportSettingsSourceType.Subsidiaries;
    }
    if (getOpportunityListFieldsKeys().includes(setting)) {
        return ExportSettingsSourceType.StaticList;
    }
    if (getScoringFieldKeys().includes(setting)) {
        return ExportSettingsSourceType.Scoring;
    }

    const accountsFields = getAccountsSourceFieldsConfig();
    const isFieldNameInAccountsConfig = accountsFields[setting];

    return isFieldNameInAccountsConfig ? ExportSettingsSourceType.Account : ExportSettingsSourceType.Contact;
};
